import { useEffect, useRef, useState } from "react";
import { cancelPendingSave, getActiveHouseholdId, loadResidenceState, saveSlice } from "./residenceSync";

/* Drop-in useState replacement for room slices.
   Backend residence_state is the authoritative store (cross-device);
   household-scoped localStorage is the fast cache / offline fallback.
   A resident edit made before backend adoption is preserved (merged onto
   the adopted remote value) so it can never clobber persisted data. */

const NS = "ara_room_state_v1";

function scopedKey(slice) {
  let hh = "";
  try { hh = window.localStorage.getItem("arawealth-household") || ""; } catch { /* ignore */ }
  return `${NS}::${hh}::${slice}`;
}

function hasContent(v) {
  if (Array.isArray(v)) return v.length > 0;
  if (v && typeof v === "object") {
    return Object.values(v).some((x) => x !== "" && x != null && !(Array.isArray(x) && x.length === 0));
  }
  return v != null && v !== "";
}

export function usePersistentState(slice, initial) {
  const [key] = useState(() => scopedKey(slice));
  const hhRef = useRef(getActiveHouseholdId());
  const initialRef = useRef(typeof initial === "function" ? initial() : initial);
  const [value, setValue] = useState(() => {
    try {
      const raw = window.localStorage.getItem(key);
      if (raw != null) return JSON.parse(raw);
    } catch { /* ignore */ }
    return initialRef.current;
  });
  const valueRef = useRef(value);
  const adoptingRef = useRef(false);
  const firstRunRef = useRef(true);
  const syncReadyRef = useRef(false);
  const preHydrationRef = useRef(null);

  // Adopt backend state once per mount; merge any pre-hydration resident edit.
  useEffect(() => {
    let alive = true;
    const hh = hhRef.current;
    if (!hh) { syncReadyRef.current = true; return undefined; }
    loadResidenceState(hh).then((slices) => {
      if (!alive) return;
      cancelPendingSave(hh, slice);
      const localSnapshot = preHydrationRef.current;
      preHydrationRef.current = null;

      if (!slices) { syncReadyRef.current = true; return; } // backend down — cache-only

      const remote = slices[slice];
      const remoteHadContent = remote && remote.data !== undefined && remote.data !== null;
      const base = remoteHadContent ? remote.data : valueRef.current;

      // A pre-hydration snapshot that diverged from the initial default is a
      // resident edit → it wins over the remote value.
      const edited = localSnapshot != null
        && JSON.stringify(localSnapshot) !== JSON.stringify(initialRef.current);
      const merged = edited ? localSnapshot : base;

      syncReadyRef.current = true;

      if (JSON.stringify(merged) !== JSON.stringify(valueRef.current)) {
        adoptingRef.current = true;
        setValue(merged);
      }
      if (edited || (!remoteHadContent && hasContent(merged))) {
        saveSlice(hh, slice, merged);
      }
    });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    valueRef.current = value;
    try { window.localStorage.setItem(key, JSON.stringify(value)); } catch { /* ignore */ }
    if (firstRunRef.current) { firstRunRef.current = false; return; }
    if (adoptingRef.current) { adoptingRef.current = false; return; }
    if (syncReadyRef.current) saveSlice(hhRef.current, slice, value);
    else preHydrationRef.current = value;
  }, [key, value, slice]);

  return [value, setValue];
}
